import { IBookingV2, IEventV2 } from '@travelnest/typewriter';
import { get } from 'lodash';
import { IPostPayment } from '../../../../data/entities/post-payment/index';
import { toFloyd } from '../../../../entities/pricing/helpers';
import { getCleaningFee } from '../finance-breakdown/helpers';

export const asFloyd = (amount: number) => +toFloyd(amount);

export const currencies = [
	'GBP', 'USD', 'EUR', 'AUD', 'BGN', 'BRL', 'CAD', 'CHF', 'CNY', 'CZK', 'DKK', 'HKD', 'HRK', 'HUF', 'IDR', 'ILS',
	'INR', 'ISK', 'JPY', 'KRW', 'MXN', 'MYR', 'NOK', 'NZD', 'PHP', 'PLN', 'RON', 'RUB', 'SEK', 'SGD', 'THB', 'TRY',
	'ZAR'
];

export interface IPostPaymentScenario {
	name: string;
	description: string;
	action_descriptions: string[];
	value_type: 'none' | 'money' | 'percentage';
	value_label?: string;
	defaultValue?: number;
	defaultReason?: string;
	buildPayments: (event: IEventV2, value: number, currency: string) => IPostPayment[];
}

export const getOwnerRentValue = (booking: IBookingV2): number => {
	return asFloyd(get(booking.value, 'breakdown.owner.discounted_owner_rent_value') || get(booking.value, 'breakdown.owner.rent_value', 0));
};

export const getChargedTouristTax = (booking: IBookingV2): {touristTax: number, convertedTouristTax: number} => {
	const touristTaxCharged = booking.value.breakdown.owner.tourist_tax_type === 'COLLECTED_ON_TOP_OF_RENT';
	return {
		touristTax: touristTaxCharged ? booking.value.breakdown.owner.tourist_tax || 0 : 0,
		convertedTouristTax: touristTaxCharged ? booking.value.breakdown.owner.converted_tourist_tax || 0 : 0
	};
};

// Guest -> affects tbv
// Owner -> affects commission

const createGuestRefund = (percentage: number) => {
	const percentageString = `${(percentage * 100).toFixed(0)}`;

	return {
		name: `Guest Cancellation (${percentageString}% refund)`,
		description: `An in-policy cancellation that will refund the guest ${percentageString}% of their payment`,
		action_descriptions: [
			`Guest will be refunded ${percentageString}% of their payment`,
			`Owner will have ${percentageString}% of their payment value for this booking reclaimed from them`
		],
		value_type: 'none',
		buildPayments: (event: IEventV2, value: number, currency: string, reason: string): IPostPayment[] => {
			const booking = event.booking || {} as IBookingV2;
			const bookingValue = booking.value || { total: 0 };
			const bookingCurrency = bookingValue.currency;
			const cleaningFee = getCleaningFee(get(bookingValue, 'breakdown.owner.extras.items', 0));
			const cashReceiptCurrency = get(bookingValue, 'breakdown.channel.cash_receipt_currency') || bookingCurrency;
			const bookingFXRate = booking.value.breakdown.channel.converted_cash_receipt_fx_rate || 1;
			const {touristTax, convertedTouristTax} = getChargedTouristTax(booking);
			const guestPaymentValue = (bookingValue.total || 0) / bookingFXRate + touristTax;

			const ownerReclaim =
				(getOwnerRentValue(booking) * percentage) +
				(asFloyd(cleaningFee) * percentage);

			return [
				// Guest refund
				{
					affects_commission: false,
					affects_tbv: true,
					amount: asFloyd(guestPaymentValue * percentage),
					currency: cashReceiptCurrency,
					direction: 'credit',
					target: 'guest',
					reason,
					created_at: new Date().toISOString()
				},
				// Owner reclaim
				{
					affects_commission: true,
					affects_tbv: false,
					amount: ownerReclaim,
					currency: bookingCurrency,
					direction: 'debit',
					target: 'owner',
					reason,
					created_at: new Date().toISOString()
				},
				// Tourist tax refund
				{
					affects_commission: false,
					affects_tbv: false,
					amount: asFloyd(convertedTouristTax),
					currency: bookingCurrency,
					direction: 'debit',
					target: 'owner',
					reason,
					created_at: new Date().toISOString(),
				},
			];
		}
	} as IPostPaymentScenario;
};

export const scenarios = {
	guest_cancellation_25: createGuestRefund(0.25),
	guest_cancellation_50: createGuestRefund(0.5),
	guest_cancellation_cleaning: {
		name: 'Guest Cancellation (50% refund + 100% Cleaning Fee)',
		description: 'An in-policy cancellation that will refund the guest 50% of their payment and the whole cleaning fee. Used for AirBnB',
		action_descriptions: [
			'Guest will be refunded 50% of their payment',
			'Guest will be refunded all of the cleaning fee',
			'Owner will have 50% of their payment value for this booking reclaimed from them'
		],
		value_type: 'none',
		buildPayments: (event: IEventV2, value: number, currency: string, reason: string): IPostPayment[] => {
			const booking = event.booking || {} as IBookingV2;
			const bookingValue = booking.value || { total: 0 };
			const bookingCurrency = bookingValue.currency;
			const cleaningFee = getCleaningFee(get(bookingValue, 'breakdown.owner.extras.items', 0));
			const cashReceiptCurrency = get(bookingValue, 'breakdown.channel.cash_receipt_currency') || bookingCurrency;
			const bookingFXRate = booking.value.breakdown.channel.converted_cash_receipt_fx_rate || 1;
			const {touristTax} = getChargedTouristTax(booking);
			const guestPaymentValue = (bookingValue.total || 0) / bookingFXRate + touristTax;
			const ownerReclaim = (getOwnerRentValue(booking) / 2) + asFloyd(cleaningFee);

			return [
				// Guest refund
				{
					affects_commission: false,
					affects_tbv: true,
					// Calculates half
					amount: asFloyd((guestPaymentValue - cleaningFee) / 2) + asFloyd(cleaningFee),
					currency: cashReceiptCurrency,
					direction: 'credit',
					target: 'guest',
					reason,
					created_at: new Date().toISOString()
				},
				// Owner reclaim
				{
					affects_commission: true,
					affects_tbv: false,
					amount: ownerReclaim,
					currency: bookingCurrency,
					direction: 'debit',
					target: 'owner',
					reason,
					created_at: new Date().toISOString()
				}
			];
		}
	} as IPostPaymentScenario,
	guest_cancellation_75: createGuestRefund(0.75),
	guest_cancellation_100: createGuestRefund(1),
	guest_cancellation_0: {
		name: 'Guest Cancellation (Cleaning Fee Refunded)',
		description: 'A cancellation within terms of AirBnB where the guest receives a refund of the cleaning fee only',
		action_descriptions: [
			'Guest will be refunded the cleaning fee for this booking',
			'Owner will have the cleaning fee for this booking reclaimed from them'
		],
		value_type: 'none',
		buildPayments: (event: IEventV2, value: number, currency: string, reason: string): IPostPayment[] => {
			const booking = event.booking || {} as IBookingV2;
			const bookingValue = booking.value || { total: 0 };
			const cleaningFee = getCleaningFee(get(bookingValue, 'breakdown.owner.extras.items', 0));
			const bookingCurrency = bookingValue.currency;

			return [
				// Guest refund
				{
					affects_commission: false,
					affects_tbv: true,
					amount: asFloyd(cleaningFee),
					currency: bookingCurrency,
					direction: 'credit',
					target: 'guest',
					reason,
					created_at: new Date().toISOString()
				},
				// Owner reclaim
				{
					affects_commission: true,
					affects_tbv: false,
					amount: asFloyd(cleaningFee),
					currency: bookingCurrency,
					direction: 'debit',
					target: 'owner',
					reason,
					created_at: new Date().toISOString()
				}
			];
		}
	} as IPostPaymentScenario,
	guest_cancellation_no_payment: {
		name: 'Guest Cancellation (Payment unsuccessful)',
		description: 'A cancellation initiated by the guest, the payment failed',
		action_descriptions: [
			'Guest will be refunded 100% of their payment',
			'Owner will have 100% of their payment value for this booking reclaimed from them'
		],
		value_type: 'none',
		buildPayments: (event: IEventV2, value: number, currency: string, reason: string): IPostPayment[] => {
			const booking = event.booking || {} as IBookingV2;
			const bookingValue = booking.value || { total: 0 };
			const bookingCurrency = bookingValue.currency;
			const cashReceiptCurrency = get(bookingValue, 'breakdown.channel.cash_receipt_currency') || bookingCurrency;
			const bookingFXRate = booking.value.breakdown.channel.converted_cash_receipt_fx_rate || 1;
			const {touristTax, convertedTouristTax} = getChargedTouristTax(booking);
			const guestPaymentValue = (bookingValue.total || 0) / bookingFXRate + touristTax;
			const cleaningFee = getCleaningFee(get(bookingValue, 'breakdown.owner.extras.items', 0));
			const nonCommissionableOPV = getOwnerRentValue(booking) + asFloyd(cleaningFee);

			return [
				// Guest refund
				{
					affects_commission: false,
					affects_tbv: true,
					amount: asFloyd(guestPaymentValue),
					currency: cashReceiptCurrency,
					direction: 'credit',
					target: 'guest',
					reason,
					created_at: new Date().toISOString()
				},
				// Owner reclaim
				{
					affects_commission: true,
					affects_tbv: false,
					amount: nonCommissionableOPV,
					currency: bookingCurrency,
					direction: 'debit',
					target: 'owner',
					reason,
					created_at: new Date().toISOString()
				},
				// Tourist tax refund
				{
					affects_commission: false,
					affects_tbv: false,
					amount: asFloyd(convertedTouristTax),
					currency: bookingCurrency,
					direction: 'debit',
					target: 'owner',
					reason,
					created_at: new Date().toISOString(),
				},
			];
		}
	} as IPostPaymentScenario,
	guest_discretionary_refund: {
		name: 'Guest Discretionary Cancellation (Refund in part or in full)',
		description: 'An out-of-policy refund to the guest initiated by the owner',
		action_descriptions: [
			'Guest will be refunded the discretionary amount',
			'Owner will have the discretionary amount reclaimed from them'
		],
		value_type: 'money',
		value_label: 'Refund Value',
		buildPayments: (event: IEventV2, value: number, currency: string, reason: string): IPostPayment[] => {
			return [
				// Guest charge
				{
					affects_commission: false,
					affects_tbv: false,
					amount: asFloyd(value),
					currency,
					direction: 'credit',
					target: 'guest',
					reason,
					created_at: new Date().toISOString()
				},
				// Owner payment
				{
					affects_commission: false,
					affects_tbv: false,
					amount: asFloyd(value),
					currency,
					direction: 'debit',
					target: 'owner',
					reason,
					created_at: new Date().toISOString()
				}
			];
		}
	} as IPostPaymentScenario,
	booking_reduction: {
		name: 'Guest length of stay reduction (Fixed Value)',
		description: 'A reduction in the value of the booking, usually used when the length of a stay is shortened',
		action_descriptions: [
			'Guest will be refunded a fixed amount',
			'Owner will have a fixed amount reclaimed from them'
		],
		value_type: 'money',
		buildPayments: (event: IEventV2, value: number, currency: string, reason: string): IPostPayment[] => {
			return [
				// Guest refund
				{
					affects_commission: false,
					affects_tbv: true,
					amount: asFloyd(value),
					currency,
					direction: 'credit',
					target: 'guest',
					reason,
					created_at: new Date().toISOString()
				},
				// Owner reclaim
				{
					affects_commission: true,
					affects_tbv: false,
					amount: asFloyd(value),
					currency,
					direction: 'debit',
					target: 'owner',
					reason,
					created_at: new Date().toISOString()
				}
			];
		}
	} as IPostPaymentScenario,
	booking_extension: {
		name: 'Guest length of stay extension (Fixed Value)',
		description: 'A increase in the value of the booking, usually used when the length of a stay is extended',
		action_descriptions: [
			'Guest will be charged a fixed amount',
			'Owner will be paid a fixed amount'
		],
		value_type: 'money',
		buildPayments: (event: IEventV2, value: number, currency: string, reason: string): IPostPayment[] => {
			return [
				// Guest charge
				{
					affects_commission: false,
					affects_tbv: true,
					amount: asFloyd(value),
					currency,
					direction: 'debit',
					target: 'guest',
					reason,
					created_at: new Date().toISOString()
				},
				// Owner payment
				{
					affects_commission: true,
					affects_tbv: false,
					amount: asFloyd(value),
					currency,
					direction: 'credit',
					target: 'owner',
					reason,
					created_at: new Date().toISOString()
				}
			];
		}
	} as IPostPaymentScenario,
	guest_complaint: {
		name: 'Guest Complaint (Fixed Value Refund)',
		description: 'A partial guest refund due to an issue with their stay',
		action_descriptions: [
			'Guest will be refunded a fixed amount',
			'Owner will be have a fixed amount reclaimed from them'
		],
		value_type: 'money',
		value_label: 'Refund Value',
		buildPayments: (event: IEventV2, value: number, currency: string, reason: string): IPostPayment[] => {
			return [
				// Guest refund
				{
					affects_commission: false,
					affects_tbv: false,
					amount: asFloyd(value),
					currency,
					direction: 'credit',
					target: 'guest',
					reason,
					created_at: new Date().toISOString()
				},
				// Owner reclaim
				{
					affects_commission: false,
					affects_tbv: false,
					amount: asFloyd(value),
					currency,
					direction: 'debit',
					target: 'owner',
					reason,
					created_at: new Date().toISOString()
				}
			];
		}
	} as IPostPaymentScenario,
	guest_damage: {
		name: 'Guest Damage Charge',
		description: 'An additional charge to cover items that have been damaged by the guest during their stay',
		action_descriptions: [
			'Guest will be charged the damage amount',
			'Owner will be paid the damage amount'
		],
		value_type: 'money',
		value_label: 'Damage Value',
		buildPayments: (event: IEventV2, value: number, currency: string, reason: string): IPostPayment[] => {
			return [
				// Guest charge
				{
					affects_commission: false,
					affects_tbv: false,
					amount: asFloyd(value),
					currency,
					direction: 'debit',
					target: 'guest',
					reason,
					created_at: new Date().toISOString()
				},
				// Owner payment
				{
					affects_commission: false,
					affects_tbv: false,
					amount: asFloyd(value),
					currency,
					direction: 'credit',
					target: 'owner',
					reason,
					created_at: new Date().toISOString()
				}
			];
		}
	} as IPostPaymentScenario,
	owner_cancellation: {
		name: 'Owner Cancellation (100% Refund)',
		description: 'A cancellation initiated by the owner that will give the guest a full refund',
		action_descriptions: [
			'Guest will be refunded 100% of their payment',
			'Owner will have 100% of their payment for this booking reclaimed from them',
		],
		value_type: 'none',
		buildPayments: (event: IEventV2, value: number, currency: string, reason: string): IPostPayment[] => {
			const booking = event.booking || {} as IBookingV2;
			const bookingValue = booking.value || { total: 0 };
			const bookingCurrency = bookingValue.currency;
			const cashReceiptCurrency = get(bookingValue, 'breakdown.channel.cash_receipt_currency') || bookingCurrency;
			const bookingFXRate = booking.value.breakdown.channel.converted_cash_receipt_fx_rate || 1;
			const {touristTax, convertedTouristTax} = getChargedTouristTax(booking);
			const guestPaymentValue = (bookingValue.total || 0) / bookingFXRate + touristTax;
			const cleaningFee = getCleaningFee(get(bookingValue, 'breakdown.owner.extras.items', 0));
			const nonCommissionableOPV = getOwnerRentValue(booking) + asFloyd(cleaningFee);

			const postPayments = [
				// Guest refund
				{
					affects_commission: false,
					affects_tbv: true,
					amount: asFloyd(guestPaymentValue),
					currency: cashReceiptCurrency,
					direction: 'credit',
					target: 'guest',
					reason,
					taxable: false,
					created_at: new Date().toISOString()
				},
				// Owner reclaim
				{
					affects_commission: true,
					affects_tbv: false,
					amount: nonCommissionableOPV,
					currency: bookingCurrency,
					direction: 'debit',
					target: 'owner',
					reason,
					taxable: false,
					created_at: new Date().toISOString()
				},
				// Tourist tax refund
				{
					affects_commission: false,
					affects_tbv: false,
					amount: asFloyd(convertedTouristTax),
					currency: bookingCurrency,
					direction: 'debit',
					target: 'owner',
					reason,
					created_at: new Date().toISOString(),
				},
			];
			return postPayments;
		}
	} as IPostPaymentScenario,
	owner_cancellation_fee: {
		name: 'Travelnest Owner Cancellation Fee',
		description: 'A Travelnest cancellation fee charged to the owner + VAT',
		action_descriptions: [
			'Owner will be charged a Travelnest cancellation fee + VAT.'],
		value_type: 'money',
		value_label: 'Cancellation Fee (excl. VAT)',
		defaultValue: 160,
		buildPayments: (event: IEventV2, value: number, currency: string, reason: string): IPostPayment[] => {
			return [
				// Owner charge
				{
					affects_commission: false,
					affects_tbv: false,
					amount: asFloyd(value),
					currency,
					direction: 'debit',
					target: 'owner',
					reason,
					taxable: true,
					created_at: new Date().toISOString()
				}
			];
		}
	} as IPostPaymentScenario,
	travelnest_commission_charge: {
		name: 'Travelnest Commission Charge',
		description: 'A booking has been transferred to owner',
		action_descriptions: [
			'Guest will be fully refunded',
			'Owner will be charged Travelnest commission'
		],
		value_type: 'none',
		buildPayments: (event: IEventV2, value: number, currency: string, reason: string): IPostPayment[] => {
			const booking = event.booking || {} as IBookingV2;
			const bookingValue = booking.value || { total: 0 };
			const bookingCurrency = bookingValue.currency;
			const cashReceiptCurrency = get(bookingValue, 'breakdown.channel.cash_receipt_currency') || bookingCurrency;
			const bookingFXRate = booking.value.breakdown.channel.converted_cash_receipt_fx_rate || 1;
			const {touristTax, convertedTouristTax} = getChargedTouristTax(booking);
			const guestPaymentValue = bookingValue.total / bookingFXRate + touristTax;

			const cleaningFee = getCleaningFee(get(bookingValue, 'breakdown.owner.extras.items', 0));
			const ownerReclaim = (getOwnerRentValue(booking) + asFloyd(cleaningFee));
			const chargeAmount = bookingValue.total * 0.05;

			return [
				// Guest refund
				{
					affects_commission: false,
					affects_tbv: true,
					amount: asFloyd(guestPaymentValue),
					currency: cashReceiptCurrency,
					direction: 'credit',
					target: 'guest',
					reason,
					created_at: new Date().toISOString()
				},
				// Wipe the existing owner payment value
				{
					affects_commission: true,
					affects_tbv: false,
					amount: ownerReclaim,
					currency: bookingCurrency,
					direction: 'debit',
					target: 'owner',
					reason,
					created_at: new Date().toISOString()
				},
				// Charge the commission
				{
					affects_commission: false,
					affects_tbv: false,
					amount: asFloyd(chargeAmount),
					currency: bookingCurrency,
					direction: 'debit',
					target: 'owner',
					reason,
					created_at: new Date().toISOString(),
					taxable: true
				},
				// Tourist tax refund
				{
					affects_commission: false,
					affects_tbv: false,
					amount: asFloyd(convertedTouristTax),
					currency: bookingCurrency,
					direction: 'debit',
					target: 'owner',
					reason,
					created_at: new Date().toISOString(),
				},
			];
		}
	} as IPostPaymentScenario,
	travelnest_discretionary_refund_100: {
		name: 'Travelnest Discretionary (100% Refund)',
		description: 'A cancellation initiated by TN that will give the guest a full refund',
		action_descriptions: [
			'Guest will be refunded 100% of their payment'
		],
		value_type: 'none',
		buildPayments: (event: IEventV2, value: number, currency: string, reason: string): IPostPayment[] => {
			const booking = event.booking || {} as IBookingV2;
			const bookingValue = booking.value || { total: 0 };
			const bookingCurrency = bookingValue.currency;
			const cashReceiptCurrency = get(bookingValue, 'breakdown.channel.cash_receipt_currency') || bookingCurrency;
			const bookingFXRate = booking.value.breakdown.channel.converted_cash_receipt_fx_rate || 1;
			const {touristTax} = getChargedTouristTax(booking);
			const guestPaymentValue = (bookingValue.total || 0) / bookingFXRate + touristTax;
			return [
				// Guest refund
				{
					affects_commission: false,
					affects_tbv: true,
					amount: asFloyd(guestPaymentValue),
					currency: cashReceiptCurrency,
					direction: 'credit',
					target: 'guest',
					reason,
					created_at: new Date().toISOString()
				}
			];
		}
	} as IPostPaymentScenario,
	travelnest_discretionary_refund: {
		name: 'Travelnest Discretionary Refund (Fixed value)',
		description: 'A refund to the guest to be covered by Travelnest',
		action_descriptions: [
			'Guest will be refunded the discretionary amount'
		],
		value_type: 'money',
		value_label: 'Refund Value',
		buildPayments: (event: IEventV2, value: number, currency: string, reason: string): IPostPayment[] => {
			return [
				// Guest charge
				{
					affects_commission: false,
					affects_tbv: true,
					amount: asFloyd(value),
					currency,
					direction: 'credit',
					target: 'guest',
					reason,
					created_at: new Date().toISOString()
				}
			];
		}
	} as IPostPaymentScenario,
	custom_owner_charge: {
		name: 'Custom Owner Charge',
		description: 'A custom charge applied to the owner',
		action_descriptions: [
			'Owner will be charged the specified amount'
		],
		value_type: 'money',
		buildPayments: (event: IEventV2, value: number, currency: string, reason: string): IPostPayment[] => {
			return [
				// Owner charge
				{
					affects_commission: false,
					affects_tbv: false,
					amount: asFloyd(value),
					currency,
					direction: 'debit',
					target: 'owner',
					reason,
					created_at: new Date().toISOString()
				}
			];
		}
	} as IPostPaymentScenario,
	custom_owner_payment: {
		name: 'Custom Owner Payment',
		description: 'A custom payment applied to the owner',
		action_descriptions: [
			'Owner will be given the specified amount'
		],
		value_type: 'money',
		buildPayments: (event: IEventV2, value: number, currency: string, reason: string): IPostPayment[] => {
			return [
				// Owner payment
				{
					affects_commission: false,
					affects_tbv: false,
					amount: asFloyd(value),
					currency,
					direction: 'credit',
					target: 'owner',
					reason,
					created_at: new Date().toISOString()
				}
			];
		}
	} as IPostPaymentScenario,
	custom_guest_charge: {
		name: 'Custom Guest Charge',
		description: 'A custom charge applied to the guest',
		action_descriptions: [
			'Guest will be charged the specified amount'
		],
		value_type: 'money',
		buildPayments: (event: IEventV2, value: number, currency: string, reason: string): IPostPayment[] => {
			return [
				// Guest charge
				{
					affects_commission: false,
					affects_tbv: false,
					amount: asFloyd(value),
					currency,
					direction: 'debit',
					target: 'guest',
					reason,
					created_at: new Date().toISOString()
				}
			];
		}
	} as IPostPaymentScenario,
	custom_guest_payment: {
		name: 'Custom Guest Payment',
		description: 'A custom payment applied to the guest',
		action_descriptions: [
			'Guest will be given the specified amount'
		],
		value_type: 'money',
		buildPayments: (event: IEventV2, value: number, currency: string, reason: string): IPostPayment[] => {
			return [
				// Guest charge
				{
					affects_commission: false,
					affects_tbv: false,
					amount: asFloyd(value),
					currency,
					direction: 'credit',
					target: 'guest',
					reason,
					created_at: new Date().toISOString()
				}
			];
		}
	} as IPostPaymentScenario,
	// tslint:disable-next-line: max-file-line-count
};
